import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["activityForm", "submitting"]
  static values = {
    extraData: Object,
    token: String,
    useWatchPosition: { type: Boolean, default: true }
  }

  connect() {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported")
    }
  }

  getLocation() {
    const activity = document.getElementById('activity').value
    if (!activity) {
      alert("Please select an activity first")
      return
    }

    this.toggleForm(false) // Hide form, show spinner

    if (this.useWatchPositionValue) {
      this.startWatchingPosition(activity)
    } else {
      this.getSinglePosition(activity)
    }
  }

  getSinglePosition(activity) {
    navigator.geolocation.getCurrentPosition(
      (position) => this.handleSuccess(position, activity),
      (error) => {
        this.toggleForm(true) // Show form again on error
        this.handleError(error)
      },
      { enableHighAccuracy: true }
    )
  }

  startWatchingPosition(activity) {
    // Clear any existing watcher
    if (this.watchId) {
      navigator.geolocation.clearWatch(this.watchId)
    }

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        // On first successful position, stop watching and submit
        navigator.geolocation.clearWatch(this.watchId)
        this.handleSuccess(position, activity)
      },
      (error) => {
        // Clear watcher on error
        navigator.geolocation.clearWatch(this.watchId)
        this.toggleForm(true) // Show form again on error
        this.handleError(error)
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
    )
  }

  handleSuccess(position, activity) {
    const { latitude, longitude, accuracy, altitude, heading, speed } = position.coords

    const data = {
      location: {
        latitude,
        longitude,
        accuracy,
        altitude,
        heading,
        speed,
        activity, // Include selected activity
        metadata: this.extraDataValue
      }
    }

    this.postLocation(data)
  }

  handleError(error) {
    let message = ""
    switch (error.code) {
      case error.PERMISSION_DENIED:
        message = "Location access was denied. Please enable location services to submit your activity."
        window.alert("Location permission required!\n\nPlease allow location access to continue.")
        break
      case error.POSITION_UNAVAILABLE:
        message = "Location information is unavailable."
        break
      case error.TIMEOUT:
        message = "The request to get location timed out."
        break
      default:
        message = "An unknown error occurred."
    }
    alert(message)
  }

  postLocation(data) {
    const csrfToken = document.querySelector("[name='csrf-token']").content
    const { token } = this.extraDataValue
    fetch(`/gps/${token}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify(data)
    })
      .then(response => {
        if (!response.ok) throw new Error("Network response was not ok")
        return response.json()
      })
      .then(() => {
        window.location.reload() // Refresh page on success
      })
      .catch(error => {
        console.error("Error:", error)
        this.toggleForm(true) // Show form again on error
        alert("Failed to save activity. Please try again.")
      })
  }

  toggleForm(showForm) {
    if (showForm) {
      this.activityFormTarget.classList.remove('hidden')
      this.submittingTarget.classList.add('hidden')
    } else {
      this.activityFormTarget.classList.add('hidden')
      this.submittingTarget.classList.remove('hidden')
    }
  }

  // Clean up any active watchers when controller disconnects
  disconnect() {
    if (this.watchId) {
      navigator.geolocation.clearWatch(this.watchId)
    }
  }
}
